import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { Route, Routes, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";

import ModuleVersionSecondaryLayout from "../components/SecondaryLayout";
import { VersionContext } from "../Context";
import ModuleVersionDependencies from "../Dependencies";
import ModuleVersionInputs from "../Inputs";
import ModuleVersionOutputs from "../Outputs";
import ModuleVersionReadme from "../Readme";
import ModuleVersionResources from "../Resources";
import { GET_VERSION_SUBMODULE } from "./gql";

const ModuleVersionSubmodule = () => {
  const { submoduleId } = useParams<{
    submoduleId: string;
  }>();

  const { module, version } = useTypedContext(VersionContext);
  const { data, error, loading } = useQuery<{ module: Module }>(GET_VERSION_SUBMODULE, {
    variables: { versionId: version.id, moduleId: module.id },
  });

  const submodules = useMemo(
    () => data?.module?.version?.metadata?.submodules || [],
    [data?.module?.version?.metadata?.submodules]
  );

  const submodule = useMemo(
    () => submodules.find((e) => e.name === submoduleId),
    [submoduleId, submodules]
  );

  const inputs = useMemo(() => submodule?.inputs || [], [submodule?.inputs]);
  const outputs = useMemo(() => submodule?.outputs || [], [submodule?.outputs]);

  const moduleDependencies = useMemo(
    () => submodule?.dependencies || [],
    [submodule?.dependencies]
  );

  const providerDependencies = useMemo(
    () => submodule?.providerDependencies || [],
    [submodule?.providerDependencies]
  );

  const resources = useMemo(() => submodule?.resources || [], [submodule?.resources]);

  const sidebar = useMemo(
    () => [
      {
        to: "..",
        label: "Readme",
        end: true,
      },
      {
        to: "../inputs",
        label: "Inputs",
        count: inputs.length,
      },
      {
        to: "../outputs",
        label: "Outputs",
        count: outputs.length,
      },
      {
        to: "../dependencies",
        label: "Dependencies",
        count: moduleDependencies.length + providerDependencies.length,
      },
      {
        to: "../resources",
        label: "Resources",
        count: resources.length || 0,
      },
    ],
    [
      inputs.length,
      outputs.length,
      resources.length,
      moduleDependencies.length,
      providerDependencies.length,
    ]
  );

  const dropdownOptions = useMemo(
    () =>
      submodules.map((e) => ({
        to: `/module/${module.id}/version/${version.id}/submodule/${e.name}`,
        isActive: e.name === submodule?.name,
        label: e.name,
      })),
    [submodules, module.id, version.id, submodule?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module) {
    return <PageLoading />;
  }

  if (!data?.module || !submodule) {
    return <NotFoundPage />;
  }

  return (
    <ModuleVersionSecondaryLayout
      entityName={submodule.name}
      title="Submodule overview"
      sidebar={sidebar}
      dropdownLabel={`Submodule: ${submodule.name}`}
      dropdownOptions={dropdownOptions}
      entityTypeLink={`/module/${module.id}/version/${version.id}/submodules`}
      entityTypeLabel="Submodules"
    >
      <Routes>
        <Route index element={<ModuleVersionReadme content={submodule.readme} />} />
        <Route path="inputs" element={<ModuleVersionInputs inputs={inputs} />} />
        <Route path="outputs" element={<ModuleVersionOutputs outputs={outputs} />} />
        <Route path="resources" element={<ModuleVersionResources resources={resources} />} />
        <Route
          path="dependencies"
          element={
            <ModuleVersionDependencies
              providerDependencies={providerDependencies}
              moduleDependencies={moduleDependencies}
            />
          }
        />
      </Routes>
    </ModuleVersionSecondaryLayout>
  );
};

export default ModuleVersionSubmodule;
